import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { createLogger } from './logger';

const logger = createLogger('Haptics');

/**
 * Haptic feedback utility for native mobile interactions
 * Gracefully falls back on web platforms
 */

/**
 * Trigger an impact haptic feedback
 * @param {string} style - 'light', 'medium', or 'heavy'
 */
export const hapticsImpact = async (style = 'medium') => {
  if (!Capacitor.isNativePlatform()) {
    return; // Silently fail on web
  }
  
  try {
    const styleMap = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    };
    
    await Haptics.impact({ 
      style: styleMap[style] || ImpactStyle.Medium 
    });
  } catch (e) {
    logger.debug('Haptics impact failed:', e);
  }
};

/**
 * Trigger a notification haptic feedback
 * @param {string} type - 'success', 'warning', or 'error'
 */
export const hapticsNotification = async (type = 'success') => {
  if (!Capacitor.isNativePlatform()) {
    return; // Silently fail on web
  }
  
  try {
    const typeMap = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error
    };
    
    await Haptics.notification({ 
      type: typeMap[type] || NotificationType.Success 
    });
  } catch (e) {
    logger.debug('Haptics notification failed:', e);
  }
};

/**
 * Trigger a selection change haptic (light impact)
 */
export const hapticsSelection = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  
  try {
    await Haptics.selectionStart();
    await Haptics.selectionChanged();
    await Haptics.selectionEnd();
  } catch (e) {
    logger.debug('Haptics selection failed:', e);
  }
};

/**
 * Vibrate the device (alternative to haptics)
 * @param {number} duration - Duration in milliseconds
 */
export const vibrate = async (duration = 200) => {
  if (!Capacitor.isNativePlatform()) {
    // Use web vibration API if available
    if (navigator.vibrate) {
      navigator.vibrate(duration);
    }
    return;
  }
  
  try {
    await Haptics.vibrate({ duration });
  } catch (e) {
    logger.debug('Vibration failed:', e);
  }
};

// Preset haptic patterns for common interactions
export const hapticPatterns = {
  buttonClick: () => hapticsImpact('light'),
  selection: () => hapticsSelection(),
  addToCart: () => hapticsImpact('medium'),
  removeFromCart: () => hapticsImpact('light'),
  checkout: () => hapticsNotification('success'),
  error: () => hapticsNotification('error'),
  warning: () => hapticsNotification('warning'),
  tabChange: () => hapticsSelection(),
  swipe: () => hapticsImpact('light'),
  refresh: () => hapticsImpact('medium'),
  likeChef: () => hapticsImpact('medium'),
  orderPlaced: () => hapticsNotification('success')
};
